import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Sun, Moon, Globe, Lock, User, Mail, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DeveloperLoginPage() {
  const { login, toggleTheme, toggleLanguage, theme, language, t } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', username: '', password: '' });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mockOtp, setMockOtp] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/developer/send-otp`, form);
      setMaskedMobile(res.data.masked_mobile || '');
      if (res.data.mock_mode && res.data.otp) setMockOtp(res.data.otp);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/developer/verify-otp`, { otp });
      login(res.data.token, res.data.user);
      navigate('/developer');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="developer-login-page">
      <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border">
        <div className="flex items-center gap-2 text-muted-foreground">
          <ShieldAlert size={16} />
          <span className="text-xs font-medium uppercase tracking-wider">Secure Access</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="dev-language-toggle" onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-secondary transition-all">
            <Globe size={12} />
            {language === 'en' ? 'தமிழ்' : 'EN'}
          </button>
          <button data-testid="dev-theme-toggle" onClick={toggleTheme}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-all">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
              <Lock size={24} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{t('devLogin')}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t('devLoginSubtitle')}</p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
            {step === 1 ? (
              <form onSubmit={handleStep1} className="space-y-4">
                <div>
                  <label className="form-label">
                    <Mail size={13} className="inline mr-1.5" />{t('email')}
                  </label>
                  <input
                    data-testid="dev-email-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="developer@email.com"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">
                    <User size={13} className="inline mr-1.5" />{t('username')}
                  </label>
                  <input
                    data-testid="dev-username-input"
                    type="text"
                    value={form.username}
                    onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                    placeholder="username"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">
                    <Lock size={13} className="inline mr-1.5" />{t('password')}
                  </label>
                  <div className="relative">
                    <input
                      data-testid="dev-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="••••••••"
                      className="form-input pr-10"
                      required
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <Lock size={14} /> : <Lock size={14} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="dev-login-error" className="px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
                    {error}
                  </div>
                )}

                <button data-testid="dev-continue-btn" type="submit" disabled={loading} className="btn-primary w-full py-3">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : t('continue')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleStep2} className="space-y-4">
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-sm">
                  <p className="text-muted-foreground">OTP sent to your registered email</p>
                  {maskedMobile && <p className="font-semibold text-foreground">{maskedMobile}</p>}
                </div>

                {mockOtp && (
                  <div data-testid="dev-mock-otp" className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">{t('mockModeAlert')}</p>
                    <p className="text-2xl font-mono font-bold text-amber-600 dark:text-amber-400 mt-1">{mockOtp}</p>
                  </div>
                )}

                <div>
                  <label className="form-label">{t('enterOtp')}</label>
                  <input
                    data-testid="dev-otp-input"
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="------"
                    className="form-input text-center text-2xl tracking-[0.5em] font-mono font-bold"
                    required
                  />
                </div>

                {error && (
                  <div data-testid="dev-otp-error" className="px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-lg border border-destructive/20">
                    {error}
                  </div>
                )}

                <button data-testid="dev-verify-btn" type="submit" disabled={loading} className="btn-primary w-full py-3">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : t('verifyOtpStep')}
                </button>
                <button type="button" onClick={() => { setStep(1); setOtp(''); setMockOtp(''); setError(''); }}
                  className="btn-secondary w-full">{t('back')}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
